<script setup>
import { ref, getCurrentInstance } from 'vue'
import { DriverStore } from '../pinia/Driver.js'

const { proxy } = getCurrentInstance()
const Driver = DriverStore()

const currency = ref('d')
const amount = ref(0)
const recipient = ref('')
const snackbar = ref(false)
const error = ref('')
const recipientRules = [
  v => /^[a-zA-Z0-9]*$/.test(v) || 'Only letters and numbers are allowed'
]

async function save() {
  error.value = ''
  
  if (amount.value <= 0) {
    error.value = 'Amount must be greater than 0'
    return
  }
  
  if (recipient.value.length === 0) {
    error.value = 'Please enter a recipient name'
    return
  }
  
  if (recipientRules[0](recipient.value) !== true) {
    error.value = recipientRules[0](recipient.value)
    return
  }

  try {
    await proxy.$http.post("", JSON.stringify({ 
      serviceName: "account", 
      methodName: "money", 
      parameters: [currency.value, amount.value, recipient.value] 
    }))

    if (currency.value == "d")
      Driver.persona.cash = Driver.persona.cash - amount.value
    else
      Driver.persona.boost = Driver.persona.boost - amount.value

    currency.value = "d"
    amount.value = 0
    recipient.value = ''
    snackbar.value = true
  } catch (err) {
    error.value = err.response?.data?.error || 'Transaction failed'
  }
}

function filterRecipientInput() {
  recipient.value = recipient.value.replace(/[^a-zA-Z0-9]/g, '')
}
</script>

<template>
<div class="konopabankForm">
  <v-snackbar :timeout="2000" color="deep-purple-accent-4" elevation="24" v-model="snackbar">
      Your transaction is successful.
  </v-snackbar>

  <v-alert class="error" v-if="error" :text="error" type="error"></v-alert>

  <!-- Level 15 Requirement -->
  <v-alert 
    v-if="Driver.persona.level < 15" 
    color="warning" 
    icon="mdi-lock"
    class="mb-4"
  >
    <div class="requirement-notice">
      <div class="notice-title">Account Transfer in Progress</div>
      <div class="notice-text">
        You are new in the city. Your bank account is currently in the process of transfer. Please come back later.
        <br><br>
        <strong>Level 15 required for the sender and recipient</strong>
      </div>
    </div>
  </v-alert>

  <v-form v-if="Driver.persona.level >= 15">
    <v-container>
      <!-- Bank Card -->
      <div class="settings-card">
        <div class="settings-card-header">
          <div class="header-icon">
            <v-icon size="28">mdi-bank</v-icon>
          </div>
          <div class="header-content">
            <h3 class="settings-title">Konopa Bank Transfer</h3>
            <p class="settings-subtitle">Send money to other drivers</p>
          </div>
        </div>

        <div class="settings-card-body">
          <!-- Currency Selection -->
          <div class="setting-item">
            <div class="setting-info full-width">
              <div class="setting-label">
                <v-icon size="20" class="mr-2">mdi-cash-multiple</v-icon>
                Select Currency
              </div>
              <div class="currency-selection">
                <div 
                  class="currency-card" 
                  :class="{'currency-selected': currency === 'd'}"
                  @click="currency = 'd'"
                >
                  <v-icon size="28" class="currency-icon">mdi-currency-usd</v-icon>
                  <div class="currency-label">Cash</div>
                  <div class="currency-amount">{{ number_format(Driver.persona.cash) }} $</div>
                </div>
                
                <div 
                  class="currency-card" 
                  :class="{'currency-selected': currency === 'b'}"
                  @click="currency = 'b'"
                >
                  <v-icon size="28" class="currency-icon">mdi-rocket-launch</v-icon>
                  <div class="currency-label">Speed Boost</div>
                  <div class="currency-amount">{{ number_format(Driver.persona.boost) }} SB</div>
                </div>
              </div>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Amount -->
          <div class="setting-item">
            <div class="setting-info full-width">
              <div class="setting-label">
                <v-icon size="20" class="mr-2">mdi-calculator</v-icon>
                Amount
              </div>
              <div class="setting-description">
                How much would you like to send?
              </div>
              <v-text-field 
                v-model="amount" 
                type="number" 
                min="0" 
                max="1500000" 
                placeholder="Enter amount" 
                variant="outlined"
                density="comfortable"
                hide-details
                class="setting-input"
              ></v-text-field>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Recipient -->
          <div class="setting-item">
            <div class="setting-info full-width">
              <div class="setting-label">
                <v-icon size="20" class="mr-2">mdi-account-arrow-right</v-icon>
                Recipient
              </div>
              <div class="setting-description">
                Enter the driver name (alphanumeric only)
              </div>
              <v-text-field 
                v-model="recipient" 
                placeholder="Recipient driver name" 
                variant="outlined"
                density="comfortable"
                hide-details
                :rules="recipientRules" 
                @input="filterRecipientInput"
                class="setting-input"
              ></v-text-field>
            </div>
          </div>
        </div>
      </div>

      <!-- Warning Notice -->
      <v-alert 
        color="error" 
        icon="mdi-alert"
        class="abuse-warning"
      >
        Any abuse for personal benefits will result in a ban.
      </v-alert>

      <!-- Send Button -->
      <v-btn 
        class="save-btn" 
        variant="elevated" 
        color="primary"
        size="large"
        @click="save"
        block
      >
        <v-icon class="mr-2">mdi-send</v-icon>
        Send Transaction
      </v-btn>
    </v-container>
  </v-form>
</div>
</template>

<style scoped>
.konopabankForm {
  margin: 0;
  padding: 0;
}

.konopabankForm .v-icon {
  color: currentColor;
  opacity: 1;
}

.settings-card {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
}

.settings-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: rgba(var(--v-theme-primary), 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(var(--v-theme-primary), 0.2);
  border-radius: 10px;
}

.header-icon .v-icon {
  color: rgb(var(--v-theme-primary));
}

.header-content {
  flex: 1;
}

.settings-title {
  margin: 0;
  font-size: 1.3em;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

.settings-subtitle {
  margin: 4px 0 0 0;
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.6);
}

.settings-card-body {
  padding: 24px;
}

.setting-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.setting-info {
  flex: 1;
}

.setting-info.full-width {
  width: 100%;
}

.setting-label {
  display: flex;
  align-items: center;
  font-size: 1em;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 6px;
}

.setting-label .v-icon {
  color: rgba(255, 255, 255, 0.9);
}

.setting-description {
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 16px;
}

.setting-input {
  margin-top: 0;
}

.currency-selection {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 8px;
}

.currency-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.currency-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(var(--v-theme-primary), 0.3);
  transform: translateY(-2px);
}

.currency-card.currency-selected {
  background: rgba(var(--v-theme-primary), 0.15);
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 16px rgba(var(--v-theme-primary), 0.3);
}

.currency-icon {
  color: rgb(var(--v-theme-primary));
}

.currency-selected .currency-icon {
  color: rgb(var(--v-theme-primary));
}

.currency-label {
  font-size: 0.9em;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.currency-amount {
  font-size: 1.1em;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
}

.requirement-notice {
  text-align: left;
}

.notice-title {
  font-size: 1em;
  font-weight: 600;
  margin-bottom: 8px;
}

.notice-text {
  font-size: 0.9em;
  line-height: 1.5;
}

.abuse-warning {
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
}

.save-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.save-btn .v-icon {
  opacity: 1 !important;
}

.error {
  margin-bottom: 20px;
}
</style>
